<template>
  <div class="data-box" :class="{ 'positive-value': isPositiveValue, 'negative-value': isNegativeValue }">
    <div class="data-box-content">
      <!-- Header Section -->
      <div class="header-section">
        <h3 class="title">{{ titleValue }}</h3>
        <div class="trend-indicator" v-if="showTrend">
          <svg class="trend-icon" :class="{ 'up': trendDirection === 'up', 'down': trendDirection === 'down' }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M7 14l5-5 5 5"/>
          </svg>
        </div>
      </div>

      <!-- Main Value Section -->
      <div class="value-section">
        <div class="number-container">
          <span class="currency-symbol" v-if="showCurrency">$</span>
          <span class="large-number">{{ formattedNumber }}</span>
          <span class="unit" v-if="unit">{{ unit }}</span>
        </div>
      </div>
      <div class="sub-value-section">
        <div class="sub-value-container" v-if="subValue && subText">
          <span class="sub-value-number" v-if="subValue">{{ subValue }}</span>
          <span class="sub-value-text" v-if="subText">{{ subText }}</span>
        </div>
      </div>

      <!-- Description Section -->
      <div class="description-section">
        <p class="description">{{ descText }}</p>
      </div>

      <!-- Decorative Elements -->
      <div class="decorative-elements">
        <div class="corner-accent top-left"></div>
        <div class="corner-accent top-right"></div>
        <div class="corner-accent bottom-left"></div>
        <div class="corner-accent bottom-right"></div>
      </div>

      <!-- Background Pattern -->
      <div class="background-pattern"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StyledDataBox',
  props: {
    titleValue: {
      type: String,
      required: true
    },
    numberValue: {
      type: [Number, String],
      required: true
    },
    descText: {
      type: String,
      required: true
    },
    unit: {
      type: String,
      default: ''
    },
    subText: {
      type: String,
      default: ''
    },
    subValue: {
      type: [Number, String],
      default: ''
    },
    showCurrency: {
      type: Boolean,
      default: false
    },
    showTrend: {
      type: Boolean,
      default: false
    },
    trendDirection: {
      type: String,
      default: 'up', // 'up' or 'down'
      validator: value => ['up', 'down'].includes(value)
    }
  },
  computed: {
    formattedNumber() {
      if (typeof this.numberValue === 'number') {
        // Format numbers with appropriate precision
        if (this.numberValue % 1 === 0) {
          return this.numberValue.toLocaleString();
        } else {
          return this.numberValue.toFixed(2).replace(/\.?0+$/, '');
        }
      }
      return this.numberValue;
    },
    isPositiveValue() {
      if (typeof this.numberValue === 'number') {
        return this.numberValue > 0;
      }
      return true;
    },
    isNegativeValue() {
      if (typeof this.numberValue === 'number') {
        return this.numberValue < 0;
      }
      return false;
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    }
  }
};
</script>

<style scoped lang="scss">
.data-box {
  margin-left: 0.9rem;
  position: relative;
  width: 100%;
  max-width: 320px;
  min-height: 150px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 
    0 4px 6px -1px var(--shadow-color),
    0 2px 4px -1px var(--shadow-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border: 1px solid var(--border-color);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 
      0 20px 25px -5px var(--shadow-color),
      0 10px 10px -5px var(--shadow-color);
    border-color: var(--border-color);
  }

  &.positive-value {
    
    .value-section .large-number {
      background: linear-gradient(135deg, var(--custom-color1) 0%, #1bb31b 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  &.negative-value {
    
    .value-section .large-number {
      background: linear-gradient(135deg, var(--custom-color1) 0%, #1bb31b 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
}

.data-box-content {
  position: relative;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 2;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.title {
  font-size: min(0.5vw, 15px);
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  flex: 1;
}

.value-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
}

.number-container {
  display: flex;
  align-items: baseline;
  gap: 4px;
  text-align: center;
}

.currency-symbol {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-tertiary);
  margin-right: 4px;
}

.large-number {
  font-size: min(1.2vw, 30px);
  font-weight: 700;
  line-height: 1;
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.unit {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-tertiary);
  margin-left: 4px;
}

.sub-value-section {
  margin: 4px 0;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-value-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(32, 204, 32, 0.03);
  border-radius: 6px;
  padding: 2px 8px;
}

.sub-value-number {
  font-size: min(0.35vw, 11px);
  font-weight: 500;
  color: var(--custom-color1);
  line-height: 1.2;
}

.sub-value-text {
  font-size: min(0.35vw, 11px);
  color: var(--text-tertiary);
  font-weight: 400;
  line-height: 1.2;
}

.description-section {
  margin-top: 16px;
}

.description {
  font-size: min(0.4vw, 12px);
  color: var(--text-tertiary);
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
  text-align: center;
}

.background-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(32, 204, 32, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(32, 204, 32, 0.03) 0%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}


@media (max-width: 750px) {
  .data-box {
    max-width: 100%;
    min-height: 120px;
    margin-bottom: 10px;
  }

  .data-box-content {
    padding: 20px;
  }

  .large-number {
    font-size: 28px;
  }

  .currency-symbol {
    font-size: 20px;
  }

  .title {
    font-size: 12px;
  }

  .description {
    font-size: 11px;
  }

  .sub-value-number {
    font-size: 10px;
  }

  .sub-value-text {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .data-box {
    min-height: 140px;
  }

  .data-box-content {
    padding: 16px;
  }

  .large-number {
    font-size: 24px;
  }

  .currency-symbol {
    font-size: 18px;
  }

  .title {
    font-size: 11px;
  }

  .description {
    font-size: 10px;
  }

  .sub-value-number {
    font-size: 9px;
  }

  .sub-value-text {
    font-size: 9px;
  }
}

// Animation for entrance
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.data-box {
  animation: fadeInUp 0.6s ease-out;
}

// Stagger animation for multiple boxes
.data-box:nth-child(1) { animation-delay: 0.1s; }
.data-box:nth-child(2) { animation-delay: 0.2s; }
.data-box:nth-child(3) { animation-delay: 0.3s; }
.data-box:nth-child(4) { animation-delay: 0.4s; }
.data-box:nth-child(5) { animation-delay: 0.5s; }
.data-box:nth-child(6) { animation-delay: 0.6s; }
</style>